import { ChangeEvent, SyntheticEvent, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SignUpFormContext } from "../sign-up-form.context";
import { RegistrationProfileForm, State } from "../sign-up-form.types";
import { SelectComponent, TextFieldComponent, TypographyComponent } from "@gx1/gx-ui-common";
import { SelectChangeEvent } from "@mui/material";
import AutocompleteV2Component from "../../../../ui/autocomplete-v2/autocomplete-v2.component";
import styles from "./sign-up-profile-fields.styles.module.scss";

const INITIAL_PROFILE: Readonly<RegistrationProfileForm> = Object.freeze({
  function: "",
  functionId: null,
  role: "",
  roleId: null,
  title: "",
  companySize: "",
  companySizeId: null,
  industry: "",
  industryId: null,
  countryCode: null,
  state: null,
});

const SignUpProfileFields = () => {
  const { t } = useTranslation();
  const { setIsCompleteProfileForm, setProfile, gcomData, setErrorText } = useContext(SignUpFormContext);
  const [currentProfile, setCurrentProfile] = useState<RegistrationProfileForm>(INITIAL_PROFILE);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // menu options
  const functionOptions = gcomData.functions.map((f) => ({ label: f.name, value: String(f.id) }));
  const roleOptionsForSelectedFunction = (gcomData.functions.find((f) => f.id === currentProfile.functionId)?.roles ?? []).map((r) => ({ label: r.name, value: String(r.id) }));
  const companySizeOptions = gcomData.companySize.map((cs) => ({ label: cs.name, value: String(cs.id) }));
  const industryOptions = gcomData.industries.map((i) => ({ label: i.name, value: String(i.id) }));
  const countryOptions = gcomData.countries.map((c) => c.isBlockedCountry ? `[BLOCKED] ${c.countryName}` : c.countryName);
  const [stateOptions, setStateOptions] = useState<State[]>([]);
  // selected options
  const selectedFunction = functionOptions.find((f) => Number(f.value) === currentProfile.functionId);
  const selectedRole = roleOptionsForSelectedFunction.find((r) => Number(r.value) === currentProfile.roleId);
  const [jobTitle, setJobTitle] = useState<string>("");
  const selectedCompanySize = companySizeOptions.find((cs) => Number(cs.value) === currentProfile.companySizeId);
  const selectedIndustry = industryOptions.find((i) => Number(i.value) === currentProfile.industryId);
  const selectedCountry = gcomData.countries.find((c) => c.countryISOCode === currentProfile.countryCode);
  const isStateRequired = selectedCountry?.isStateMandatory;
  const selectedState = stateOptions.find((s) => s.stateISOCode === currentProfile.state?.stateCode);

  const isProfileComplete = Boolean(
    selectedFunction
    && selectedRole
    && jobTitle.length > 1
    && selectedCompanySize
    && selectedIndustry
    && selectedCountry
    && (isStateRequired ? selectedState : true)
  );

  useEffect(() => {
    if (isProfileComplete) {
      setProfile(currentProfile);
    }
    setIsCompleteProfileForm(isProfileComplete);
  }, [currentProfile, isProfileComplete, setIsCompleteProfileForm, setProfile]);

  useEffect(() => {
    if (!(selectedCountry && selectedCountry.isStateMandatory)) {
      return;
    }
    void (async () => {
      try {
        const response = await window.fetch(`${window.gxApiConnection}/api/v2/data/state/${selectedCountry.countryISOCode}`, {
          method: "GET",
          credentials: "include",
        });
        const resJson = await response.json();
        if (response?.status !== 200 || !resJson?.data) {
          throw new Error();
        }
        setStateOptions(resJson.data.states);
        containerRef.current?.scroll({ behavior: "smooth", top: containerRef.current?.clientHeight });
      } catch {
        setErrorText(t("Oops... something went wrong. Please try again."));
      }
    })();
  }, [isStateRequired, selectedCountry, setErrorText, t]);

  const handleFunctionChange = (e: SelectChangeEvent<string>) => {
    const selectedOption = gcomData.functions.find((i) => String(i.id) === e.target.value);
    if (selectedOption) {
      setCurrentProfile((p: RegistrationProfileForm) => ({ ...p, function: selectedOption.name, functionId: Number(selectedOption.id) }));
    }
  };

  const handleRoleChange = (e: SelectChangeEvent<string>) => {
    const selectedOption = (gcomData.functions.find((f) => f.id === currentProfile.functionId)?.roles || []).find((i) => String(i.id) === e.target.value);
    if (selectedOption) {
      setCurrentProfile((p: RegistrationProfileForm) => ({ ...p, role: selectedOption.name, roleId: Number(selectedOption.id) }));
    }
  };

  const handleJobTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const targetValue = e.target.value;
    setJobTitle(targetValue);
    setCurrentProfile((p: RegistrationProfileForm) => ({ ...p, title: targetValue }));
  };

  const handleCompanySizeChange = (e: SelectChangeEvent<string>) => {
    const selectedOption = gcomData.companySize.find((i) => String(i.id) === e.target.value);
    if (selectedOption) {
      setCurrentProfile((p: RegistrationProfileForm) => ({ ...p, companySize: selectedOption.name, companySizeId: Number(selectedOption.id) }));
    }
  };

  const handleIndustryChange = (e: SelectChangeEvent<string>) => {
    const selectedOption = gcomData.industries.find((i) => String(i.id) === e.target.value);
    if (selectedOption) {
      setCurrentProfile((p: RegistrationProfileForm) => ({ ...p, industry: selectedOption.name, industryId: Number(selectedOption.id) }));
    }
  };

  const handleCountryChange = async (_e: SyntheticEvent, value: string | null) => {
    clearState();
    const selectedOption = gcomData.countries.find((c) => c.countryName === value);
    if (selectedOption) {
      setCurrentProfile((p: RegistrationProfileForm) => ({ ...p, countryCode: selectedOption.countryISOCode }));
    }
  };

  const handleStateChange = (_e: SyntheticEvent, value: string | null) => {
    const selectedOption = stateOptions.find((s) => s.stateName === value);
    if (selectedOption) {
      setCurrentProfile((p: RegistrationProfileForm) => ({
        ...p,
        state: {
          isProvince: selectedCountry?.countryISOCode?.toUpperCase() !== "US",
          stateName: selectedOption.stateName,
          stateCode: selectedOption.stateISOCode,
        },
      }));
    }
  };

  const clearState = () => {
    setStateOptions([]);
    setCurrentProfile((p: RegistrationProfileForm) => ({ ...p, state: undefined }));
  };

  return (
    <div aria-label={"profileContainer"} ref={containerRef} className={styles.profileContainer}>
      <TypographyComponent styling={"p-xs"} color={"grey7"} boldness={"semi"} rootClassName={styles.groupTitle}>
        {t("About you")}
      </TypographyComponent>
      <div className={styles.groupFields}>
        <SelectComponent
          label={t("Function")}
          menuItems={functionOptions}
          onChange={handleFunctionChange}
          value={selectedFunction?.value || ""}
        />
        <SelectComponent
          label={t("Role")}
          isDisabled={roleOptionsForSelectedFunction.length === 0}
          menuItems={roleOptionsForSelectedFunction}
          onChange={handleRoleChange}
          value={selectedRole?.value || ""}
        />
        <TextFieldComponent
          placeholder={t("Job Title")}
          onChange={handleJobTitleChange}
          value={jobTitle}
        />
      </div>
      <TypographyComponent styling={"p-xs"} color={"grey7"} boldness={"semi"} rootClassName={styles.groupTitle}>
        {t("About your company")}
      </TypographyComponent>
      <div className={styles.groupFields}>
        <SelectComponent
          label={t("Company Size")}
          menuItems={companySizeOptions}
          value={selectedCompanySize?.value || ""}
          onChange={handleCompanySizeChange}
        />
        <SelectComponent
          label={t("Industry")}
          menuItems={industryOptions}
          onChange={handleIndustryChange}
          value={selectedIndustry?.value || ""}
        />
        <AutocompleteV2Component
          label={t("Country")}
          options={countryOptions}
          onChange={handleCountryChange}
          value={selectedCountry?.countryName ?? null}
        />
        {isStateRequired && (
          <AutocompleteV2Component
            label={t("State, Province or Region")}
            options={stateOptions.map((state) => state.stateName)}
            onChange={handleStateChange}
            value={selectedState?.stateName ?? null}
          />
        )}
      </div>
    </div>
  );
};

export default SignUpProfileFields;
